import {
  Autocomplete,
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import escapeCharacters from "../../../helpers/escapeCharacters";
import InventoryTracking from "../../../api/InventoryTracking";
import { useTheme } from "@emotion/react";
import { DarkLightModeContext } from "../../../context/DarkLightModeContext";
import { Add, Close, Edit } from "@mui/icons-material";
import InventoryItem from "./InventoryItem";
import Vendor from "./Vendor";
import DeliveredItem from "./DeliveredItem";
import StagedItem from "./StagedItem";
import TruckItem from "./TruckItem";
import CommittedItem from "./CommittedItem";

const ReturnItemDetailsCard = ({
  isEditable,
  jobId,
  description,
  setDescription,
  quantity,
  setQuantity,
  returnFrom,
  setReturnFrom,
  returnFromId,
  setReturnFromId,
  returnTo,
  setReturnTo,
  inventoryItemId,
  setInventoryItemId,
  vendorId,
  setVendorId,
  uom,
  setUom,
  price,
  setPrice,
  onRemove,
}) => {
  const { isDarkMode } = useContext(DarkLightModeContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { authToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [inventoryList, setInventoryList] = useState([]);
  const [inventorySearch, setInventorySearch] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [vendorList, setVendorList] = useState([]);
  const [deliveredSearch, setDeliveredSearch] = useState("");
  const [deliveredItems, setDeliveredItems] = useState([]);

  const searchDeliveredItem = async (controller) => {
    try {
      setLoading(true);
      let headers = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      if (controller) {
        headers.signal = controller.signal;
      }
      const query = escapeCharacters(deliveredSearch);
      const response = await InventoryTracking.get(
        `/jobs/${jobId}/delivered-items?search=${query}`,
        headers,
      );
      let data = response.data.delivered;
      for (let item of data) {
        item.label = `${item.description} [${item.material_origin_key} - qty: ${item.quantity} ${item.uom}]`;
      }

      setLoading(false);
      setDeliveredItems([...data]);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const searchInventoryItem = async (controller) => {
    try {
      setLoading(true);
      let headers = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      if (controller) {
        headers.signal = controller.signal;
      }
      const query = escapeCharacters(inventorySearch);
      const response = await InventoryTracking.get(
        `/inventory?search=${query}&location=SDL&pageSize=20&page=${page}`,
        headers,
      );
      let data = {
        items: response.data.materials,
        pages: response.data.pages,
      };
      for (let item of data.items) {
        item.label = `[${item.internal_code}] ${item.description}`;
      }

      setLoading(false);
      setInventoryList([...data.items]);
      setPages(data.pages);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    searchInventoryItem(controller);
    setPage(1);
    return () => {
      controller.abort(); // abort api call on unmount for cleanup
    };
  }, [inventorySearch]);

  useEffect(() => {
    if (returnFrom == "DELIVERED") searchDeliveredItem();
  }, [jobId, deliveredSearch, returnFrom]);

  return (
    <Box sx={{ marginTop: "16px" }}>
      <Box>
        <Box sx={{}}>
          <Box sx={{}}>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              {returnFromId ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "row",
                    padding: "12px",
                    border: 1,
                    borderColor: isDarkMode
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.2)",
                    borderRadius: "4px",
                    paddingTop: "16px",
                    marginTop: "1px",
                  }}
                >
                  <Typography sx={{ fontWeight: "bold", marginRight: "8px" }}>
                    Item:
                  </Typography>
                  <DeliveredItem
                    sx={{
                      flex: 1,
                      //   overflow: "hidden",
                      //   textOverflow: "ellipsis",
                      //   display: "-webkit-box",
                      //   WebkitLineClamp: "1",
                      //   WebkitBoxOrient: "vertical",
                    }}
                    jobId={jobId}
                    deliveredId={returnFromId}
                  />
                  {isEditable && (
                    <Edit
                      onClick={() => {
                        setReturnFromId(null);
                        setDescription("");
                        setInventoryItemId(null);
                      }}
                    />
                  )}
                </Box>
              ) : (
                <Box sx={{ flex: 1 }}>
                  <Autocomplete
                    disabled={!isEditable}
                    fullWidth={true}
                    sx={{
                      marginRight: isSmallScreen ? 0 : "8px",
                      marginTop: isSmallScreen ? "8px" : 0,
                    }}
                    disablePortal
                    inputValue={deliveredSearch}
                    onInputChange={(event, newInputValue) => {
                      setDeliveredSearch(newInputValue);
                    }}
                    options={deliveredItems}
                    getOptionDisabled={(option) => {
                      if (
                        !option.item_id ||
                        option.material_origin_key !== "INVENTORY"
                      )
                        return true;
                      else return false;
                    }}
                    renderInput={(params) => (
                      <TextField
                        disabled={!isEditable}
                        fullWidth
                        {...params}
                        label="Search & Match Delivered Item"
                      />
                    )}
                    onChange={(event, value) => {
                      console.log("ONCHANGE", value);
                      if (value) {
                        setReturnFromId(value.id);
                        setDescription(value.description);
                        setUom(value.uom_id);
                        setInventoryItemId(value.item_id);
                      }
                    }}
                  />
                </Box>
              )}
              <TextField
                disabled={!isEditable}
                sx={{ width: "100px", marginLeft: "8px" }}
                name="Quantity"
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => {
                  setQuantity(e.target.value);
                }}
              />
              {isEditable && (
                <IconButton
                  sx={{ marginLeft: "8px" }}
                  variant="text"
                  onClick={() => {
                    onRemove();
                  }}
                >
                  <Close />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default ReturnItemDetailsCard;
